import { SeoLandingLayout, SeoLandingPageData } from '../../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'BFR et trésorerie : comprendre et réduire son besoin en fonds de roulement | Trezoone',
    description: 'Le BFR (Besoin en Fonds de Roulement) est le cash immobilisé dans votre cycle d\'exploitation. Apprenez à le calculer, le suivre et le réduire.',
    canonical: '/bfr-tresorerie',
    keywords: 'BFR trésorerie, besoin en fonds de roulement, calcul BFR, réduire BFR, BFR PME, fonds de roulement négatif',
  },
  hero: {
    tag: 'BFR et trésorerie',
    title: 'Votre entreprise est rentable mais manque de cash. C\'est le BFR.',
    subtitle: 'Le Besoin en Fonds de Roulement, c\'est le cash que vous devez avancer avant de l\'encaisser. Comprendre et maîtriser votre BFR, c\'est sécuriser votre trésorerie.',
    ctaLabel: 'Calculer mon BFR',
    ctaHref: '/simulateur-tresorerie-association',
  },
  pilierLink: { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-pme' },
  sections: [
    {
      type: 'explain',
      title: 'Qu\'est-ce que le BFR et comment le calculer ?',
      content: 'Le BFR mesure le décalage entre les flux entrants (encaissements clients) et les flux sortants (paiements fournisseurs + salaires).',
      items: [
        'BFR = Créances clients + Stocks - Dettes fournisseurs',
        'Si BFR > 0 : vous avancez du cash → vous avez besoin de financement',
        'Si BFR < 0 : vos clients paient avant que vous payiez vos fournisseurs → situation favorable (GMS, e-commerce)',
        'Exemple : créances 80 000 € + stocks 20 000 € - dettes 40 000 € = BFR de 60 000 €',
        'Ces 60 000 € doivent être financés en permanence par votre trésorerie ou vos crédits',
      ],
    },
    {
      type: 'example',
      title: 'Impact du BFR sur la trésorerie : exemple chiffré',
      content: 'Une PME voit son CA passer de 500 000 € à 750 000 € (+50 %). Bonne nouvelle ? Pas seulement.',
      items: [
        'Avec des délais clients de 60 jours, les créances passent de 85 000 € à 125 000 €',
        'Le BFR augmente de 40 000 € supplémentaires',
        'La croissance consomme 40 000 € de cash — sans investissement ni recrutement',
        'Résultat fréquent : PME en croissance forte avec trésorerie en tension',
        'Solution : anticiper la croissance du BFR dans le plan de trésorerie',
      ],
    },
    {
      type: 'midcta',
      title: 'Votre croissance consomme du cash sans que vous le voyiez — maîtrisez votre BFR maintenant',
      content: 'Trezoone calcule votre BFR en temps réel et vous alerte dès qu\'il dépasse le seuil tolérable pour votre trésorerie.',
    },
    {
      type: 'checklist',
      title: '6 actions pour réduire votre BFR',
      items: [
        'Négocier des délais de paiement fournisseurs plus longs (30j → 45j = cash libéré)',
        'Réduire les délais de paiement clients (factures dès livraison, conditions strictes)',
        'Mettre en place des relances clients automatiques dès J+1 de retard',
        'Demander des acomptes sur les commandes importantes',
        'Réduire les stocks en flux tendus si votre activité le permet',
        'Financer le BFR avec un crédit de trésorerie dédié plutôt que le découvert',
      ],
    },
    {
      type: 'solution',
      title: 'Trezoone suit votre BFR en temps réel',
      content: 'Trezoone calcule et affiche votre BFR automatiquement à partir de vos données réelles.',
      items: [
        'Calcul du BFR en temps réel intégré au tableau de bord',
        'Alerte si le BFR dépasse un seuil défini',
        'Simulation de l\'impact d\'une réduction des délais clients sur le cash',
        'Plan de trésorerie intégrant l\'évolution du BFR lors de phases de croissance',
      ],
    },
    {
      type: 'cluster',
      title: 'Ressources liées à la trésorerie d\'entreprise',
      links: [
        { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-tpe' },
        { label: 'Plan trésorerie PME', href: '/plan-tresorerie-pme' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Logiciel trésorerie entreprise', href: '/logiciel-tresorerie-entreprise' },
        { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
        { label: 'Problème cash flow', href: '/probleme-cash-flow' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
        { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
      ],
    },
    {
      type: 'cta',
      title: 'Maîtrisez votre BFR avec Trezoone',
      content: 'Calculez, suivez et simulez votre BFR. Essai gratuit 14 jours.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-tpe' },
    { label: 'Plan trésorerie PME', href: '/plan-tresorerie-pme' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Logiciel trésorerie entreprise', href: '/logiciel-tresorerie-entreprise' },
    { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
    { label: 'Problème cash flow', href: '/probleme-cash-flow' },
    { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
    { label: 'Simulateur trésorerie', href: '/simulateur-tresorerie-association' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }
export default function BFRTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
